<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcProductMiniCard',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    item: {
      type: Object,
      required: true,
    },
    noIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    noLink: {
      type: Boolean,
      required: false,
      default: false,
    },
    noStrong: {
      type: Boolean,
      required: false,
      default: false,
    },
    linkStyleUnderline: {
      type: Boolean,
      required: false,
      default: false,
    },
    linkStyleIcon: {
      type: Boolean,
      required: false,
      default: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );
  watch(
    () => props.noIcon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noIcon');
    }
  );
  watch(
    () => props.noLink,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noLink');
    }
  );
  watch(
    () => props.noStrong,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('noStrong');
    }
  );
  watch(
    () => props.linkStyleUnderline,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('linkStyleUnderline');
    }
  );
  watch(
    () => props.linkStyleIcon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('linkStyleIcon');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center">
    <ItpText
      :strong="!noStrong"
      :text="item.serial_number"
      class="me-2"
      v-if="noLink || !linkStyleUnderline"
    >
    </ItpText>
    <ItpLink
      :to="{ name: 'app.product', params: { id: item.id } }"
      v-if="!noLink && linkStyleUnderline"
    >
      <ItpText :strong="!noStrong" :text="item.serial_number" class="me-2">
      </ItpText>
    </ItpLink>
    <ItpLink
      :icon="$config.externalLinkIcon"
      :to="{ name: 'app.product', params: { id: item.id } }"
      v-if="!noLink && linkStyleIcon && !linkStyleUnderline"
    >
    </ItpLink>
  </ItpBox>
</template>
